import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { User, Phone, Copy, MessageCircle, Youtube } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function StudentMentors({ user, onLogout }) {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/mentors`, { headers: getAuthHeaders() });
      setMentors(response.data); // Students only see active mentors
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
      toast.error('Failed to load mentors');
    } finally {
      setLoading(false);
    }
  };

  const handleCallMentor = (mentor) => {
    setSelectedMentor(mentor);
    
    // Check if device supports tel: links
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Direct call on mobile
      window.location.href = `tel:${mentor.phone_number}`;
    } else {
      // Show modal on desktop
      setShowCallModal(true);
    }
  };

  const copyPhoneNumber = (phoneNumber) => {
    navigator.clipboard.writeText(phoneNumber).then(() => {
      toast.success('Phone number copied to clipboard!');
    });
  };

  const openDialer = (phoneNumber) => {
    // Try to open dialer
    window.location.href = `tel:${phoneNumber}`;
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="help"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="help">
      <div className="p-6 lg:p-8 space-y-6" data-testid="student-mentors">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-navy mb-4">Get Help from Mentors</h1>
          <p className="text-[#4D4D4D] text-lg font-light max-w-2xl mx-auto">
            Connect with our experienced mentors who are here to support your learning journey. 
            Click to call any mentor for personalized guidance and assistance.
          </p>
        </div>

        {/* Mentors Grid */}
        {mentors.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-16 text-center">
              <User className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Mentors Available</h3>
              <p className="text-[#4D4D4D] font-light">
                Mentors will appear here once they are added by your administrators.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mentors.map((mentor) => (
              <Card key={mentor.id} className="border-0 card-shadow bg-white rounded-2xl overflow-hidden card-hover transition-smooth group">
                <CardContent className="p-0">
                  {/* Large Rectangular Photo */}
                  <div className="relative h-64 bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden">
                    {mentor.photo_url ? (
                      <img 
                        src={mentor.photo_url} 
                        alt={`${mentor.full_name}'s photo`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-blue to-[#1874CD] flex items-center justify-center">
                        <User className="w-24 h-24 text-white opacity-50" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>

                  {/* Mentor Info */}
                  <div className="p-6 space-y-4">
                    <div className="text-center">
                      <h2 className="text-xl font-bold text-navy">{mentor.full_name}</h2>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {/* Call Button */}
                      <Button
                        onClick={() => handleCallMentor(mentor)}
                        className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 px-4 rounded-xl transition-smooth btn-press flex items-center justify-center space-x-2"
                      >
                        <Phone className="w-4 h-4" />
                        <span>Call</span>
                      </Button>

                      {/* WhatsApp Button */}
                      {mentor.whatsapp_number && (
                        <Button
                          onClick={() => window.open(`https://wa.me/${mentor.whatsapp_number.replace(/[^0-9]/g, '')}`, '_blank')}
                          className="w-full bg-[#E8F5E9] hover:bg-[#C8E6C9] text-[#2E7D32] font-semibold py-3 px-4 rounded-xl transition-smooth btn-press flex items-center justify-center space-x-2"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>WhatsApp</span>
                        </Button>
                      )}

                      {/* YouTube Button */}
                      {mentor.youtube_link && (
                        <Button
                          onClick={() => window.open(mentor.youtube_link, '_blank')}
                          className="w-full bg-[#FFEBEE] hover:bg-[#FFCDD2] text-[#D32F2F] font-semibold py-3 px-4 rounded-xl transition-smooth btn-press flex items-center justify-center space-x-2"
                        >
                          <Youtube className="w-4 h-4" />
                          <span>YouTube</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Desktop Call Modal */}
        <Dialog open={showCallModal} onOpenChange={setShowCallModal}>
          <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-navy flex items-center space-x-2">
                <Phone className="w-6 h-6" />
                <span>Contact Mentor</span>
              </DialogTitle>
            </DialogHeader>
            
            {selectedMentor && (
              <div className="space-y-6">
                {/* Mentor Info */}
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden bg-blue-100 flex items-center justify-center">
                    {selectedMentor.photo_url ? (
                      <img 
                        src={selectedMentor.photo_url} 
                        alt={selectedMentor.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-primary-blue" />
                    )}
                  </div>
                  <h3 className="font-bold text-navy">{selectedMentor.full_name}</h3>
                  <div className="mt-4 p-3 bg-white rounded-xl border-2 border-blue-200">
                    <div className="flex items-center justify-center space-x-2">
                      <Phone className="w-4 h-4 text-primary-blue" />
                      <span className="text-lg font-semibold text-navy">
                        {selectedMentor.phone_number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={() => {
                      openDialer(selectedMentor.phone_number);
                      setShowCallModal(false);
                    }}
                    className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 px-4 rounded-xl transition-smooth flex items-center justify-center space-x-2"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Call Now</span>
                  </Button>
                  
                  <Button
                    onClick={() => {
                      copyPhoneNumber(selectedMentor.phone_number);
                      setShowCallModal(false);
                    }}
                    variant="outline"
                    className="w-full border-2 border-primary-blue text-primary-blue hover:bg-blue-50 font-semibold py-3 px-4 rounded-xl transition-smooth flex items-center justify-center space-x-2"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Number</span>
                  </Button>
                  
                  <Button
                    onClick={() => setShowCallModal(false)}
                    variant="outline"
                    className="w-full border-2 border-[#E7E7E7] text-navy hover:bg-gray-50 font-semibold py-3 px-4 rounded-xl transition-smooth"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}